/**
 * <card-swatches>: colour dots on a product card (feature 016, specs/016-product-card-redesign/contracts/product-card.md §4).
 * Hover or focus previews a colour's photo; leaving restores the selected (or default) photo.
 * Click, Enter or Space selects: the photo stays and the card's links open that variant.
 * One tab stop per group (roving tabindex); arrow keys, Home and End move between dots.
 */
if (!customElements.get('card-swatches')) {
  class CardSwatches extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.gift-card');
      this.dots = [...this.querySelectorAll('.card-swatch')];
      this.image = this.card?.querySelector('.gift-card__image:not(.gift-card__image--secondary)');
      this.links = this.card ? [...this.card.querySelectorAll('.gift-card__title, .gift-card__media-link')] : [];
      if (!this.image || !this.dots.length) return;
      this.original = {
        src: this.image.getAttribute('src'),
        srcset: this.image.getAttribute('srcset'),
        href: this.links[0]?.getAttribute('href'),
      };
      this.selected = null;

      this.addEventListener('pointerover', (event) => {
        const dot = event.target.closest('.card-swatch');
        if (dot) this.preview(dot);
      });
      this.addEventListener('pointerleave', () => this.restore());
      this.addEventListener('focusin', (event) => {
        const dot = event.target.closest('.card-swatch');
        if (dot) this.preview(dot);
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.restore();
      });
      this.addEventListener('click', (event) => {
        const dot = event.target.closest('.card-swatch');
        if (!dot) return;
        event.preventDefault();
        this.select(dot);
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    showPhoto(dot) {
      if (dot && dot.dataset.mediaSrc) {
        this.image.setAttribute('src', dot.dataset.mediaSrc);
        this.image.setAttribute('srcset', dot.dataset.mediaSrcset || dot.dataset.mediaSrc);
      } else {
        this.image.setAttribute('src', this.original.src);
        if (this.original.srcset) this.image.setAttribute('srcset', this.original.srcset);
      }
    }

    preview(dot) {
      this.card.dataset.swatchPreview = '';
      this.showPhoto(dot);
    }

    restore() {
      this.showPhoto(this.selected);
      if (!this.selected) delete this.card.dataset.swatchPreview;
    }

    select(dot) {
      this.selected = dot;
      this.dots.forEach((other) => other.setAttribute('aria-pressed', String(other === dot)));
      const href = dot.dataset.url || this.original.href;
      this.links.forEach((link) => link.setAttribute('href', href));
      this.card.dataset.swatchPreview = '';
      this.showPhoto(dot);
    }

    onKeydown(event) {
      const current = this.dots.indexOf(document.activeElement);
      if (current === -1) return;
      const last = this.dots.length - 1;
      const next = {
        ArrowDown: Math.min(current + 1, last),
        ArrowRight: Math.min(current + 1, last),
        ArrowUp: Math.max(current - 1, 0),
        ArrowLeft: Math.max(current - 1, 0),
        Home: 0,
        End: last,
      }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      this.dots.forEach((dot, index) => dot.setAttribute('tabindex', index === next ? '0' : '-1'));
      this.dots[next].focus();
    }
  }

  customElements.define('card-swatches', CardSwatches);
}
